// ─────────────────────────────────────────────────────────────────────────────
// src/features/profile/ProfilePage.tsx
//
// Page Profil utilisateur — design Nawara BI
//   • Upload photo via le backend Avsoft → POST {url_backend_bi}/api/PieceJointe/upload/photo
//     (le backend gère lui-même Cloudinary — aucun appel direct)
//   • URL backend lue depuis parametre_bi → GET /api/auth/config → url_backend_bi
//   • Photo persistée dans localStorage sous deux clés :
//       - bi_user.photoUrl          (session courante)
//       - bi_photo_{username}       (clé dédiée, survit au logout/login)
//     → AuthPage doit restaurer bi_photo_{username} dans bi_user après login
//   • Champs Email / Téléphone éditables inline (✎ → édition → ✕ annule / ✓ valide)
//     → PUT {BI_API_URL}/profile/update-user (relayé par UtilisateurBiController
//     vers {url_backend_bi}/Utilisateur/update-user). Identifiant et Dépôt restent
//     en lecture seule car non supportés par UpdateProfileRequest côté backend.
// ─────────────────────────────────────────────────────────────────────────────

import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import { updateUserProfile, uploadProfilePhoto } from "../features/profile/api";
import { getErrorMessage } from "../shared/api/http";
import { session } from "../shared/api/session";
import { Spinner as BaseSpinner } from "../shared/ui/Spinner";
import { colors } from "../shared/ui/tokens";

// ── Nawara brand colours ────────────────────────────────────────────────────
const NW_BRAND = {
  bgDeep: colors.brand[600],
  bgMid: colors.brand[500],
  bgLight: colors.brand[500],
  mauve: colors.brand[300],
  mauveGlow: colors.brand[400],
  mauveDim: "rgba(125,168,217,0.18)",
  mauveBorder: "rgba(125,168,217,0.35)",
  violet: colors.brand[700],
  violetDark: colors.brand[700],
  white: colors.neutral[0],
  dimWhite: "rgba(255,255,255,0.35)",
  faintLine: "rgba(125,168,217,0.15)",
  textPrimary: colors.brand[900],
  textSecondary: colors.brand[800],
  textMuted: colors.brand[400],
  textLight: "rgba(19,59,110,0.45)",
  danger: colors.danger[500],
  dangerLight: colors.danger[50],
  success: colors.accent[500],
  successLight: colors.accent[100],
};

// ── Helpers localStorage ─────────────────────────────────────────────────────
function getBiUser(): Record<string, any> | null {
  return session.getUser<Record<string, any>>();
}

function saveBiUser(user: Record<string, any>) {
  session.setUser(user);
}

// ── Clé dédiée photo — format : bi_photo_{username} ──────────────────────────
// Cette clé est indépendante de bi_user (écrasé à chaque login).
// AuthPage doit lire cette clé après login et injecter photoUrl dans bi_user.
export function getPhotoStorageKey(user: Record<string, any> | null): string {
  const id =
    user?.username || user?.Username || user?.email || user?.Email || "default";
  return `bi_photo_${id}`;
}

function savePhotoUrl(user: Record<string, any> | null, url: string) {
  // a) Dans bi_user → affichage immédiat session courante
  const current = getBiUser() ?? {};
  saveBiUser({ ...current, photoUrl: url });
  // b) Dans la clé dédiée → persistance entre sessions
  localStorage.setItem(getPhotoStorageKey(user), url);
}

function loadPersistedPhoto(user: Record<string, any> | null): string {
  const fromUser = user?.photoUrl || user?.PhotoUrl || "";
  if (fromUser) return fromUser;
  // Tenter de restaurer depuis la clé dédiée
  const fromKey = localStorage.getItem(getPhotoStorageKey(user)) ?? "";
  if (fromKey && user) saveBiUser({ ...user, photoUrl: fromKey });
  return fromKey;
}

// ── InfoField ────────────────────────────────────────────────────────────────
const InfoField: React.FC<{
  label: string;
  value: string | undefined | null;
  icon: string;
}> = ({ label, value, icon }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 14,
      padding: "14px 20px",
      borderRadius: 12,
      background: "rgba(125,168,217,0.06)",
      border: `1px solid ${NW_BRAND.faintLine}`,
    }}
  >
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: 10,
        background: `linear-gradient(135deg, ${NW_BRAND.bgMid}, ${NW_BRAND.bgLight})`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 17,
        flexShrink: 0,
        boxShadow: `0 0 10px ${NW_BRAND.mauveDim}`,
      }}
    >
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: NW_BRAND.textMuted,
          textTransform: "uppercase",
          letterSpacing: 1.2,
          marginBottom: 3,
          fontFamily: "Inter, system-ui, -apple-system, sans-serif",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: value ? NW_BRAND.textPrimary : NW_BRAND.textLight,
          fontFamily: "Inter, system-ui, -apple-system, sans-serif",
          wordBreak: "break-word",
        }}
      >
        {value || "—"}
      </div>
    </div>
  </div>
);

// ── Icône caméra SVG ─────────────────────────────────────────────────────────
const CameraIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 15,
  color = "#FFFFFF",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

// ── Spinner ──────────────────────────────────────────────────────────────────
// Variante Profil du spinner partagé : anneau translucide de la même couleur
const Spinner: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = NW_BRAND.violet,
}) => (
  <BaseSpinner
    size={size}
    color={color}
    trackColor={`${color}33`}
    thickness={2.5}
    speed={0.65}
  />
);

// ─────────────────────────────────────────────────────────────────────────────
// EditableInfoField — champ avec édition inline (✎ → input → ✕ / ✓)
//   • État initial : affichage simple + bouton ✎ (crayon)
//   • Clic ✎  → passe en mode édition (input texte focus automatique)
//   • Clic ✕  → annule, revient à l'état initial (valeur d'origine, non éditable)
//   • Clic ✓  → appelle onSave(fieldKey, valeur) ; si succès → revient en lecture
//     seule avec la nouvelle valeur ; si erreur → reste en édition et affiche
//     le message d'erreur sous le champ
// ─────────────────────────────────────────────────────────────────────────────
interface EditableInfoFieldProps {
  label: string;
  value: string;
  icon: string;
  fieldKey: string;
  onSave: (fieldKey: string, newValue: string) => Promise<void>;
}

const EditableInfoField: React.FC<EditableInfoFieldProps> = ({
  label,
  value,
  icon,
  fieldKey,
  onSave,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Resynchroniser le brouillon si la valeur externe change (après save réussi)
  useEffect(() => {
    if (!isEditing) setDraft(value || "");
  }, [value, isEditing]);

  const handleEditClick = () => {
    setDraft(value || "");
    setError(null);
    setIsEditing(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleCancel = () => {
    setDraft(value || "");
    setError(null);
    setIsEditing(false);
  };

  const handleConfirm = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(fieldKey, draft.trim());
      setIsEditing(false);
    } catch (err: any) {
      setError(err?.message || "Erreur lors de la mise à jour.");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleConfirm();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        padding: "14px 20px",
        borderRadius: 12,
        background: "rgba(125,168,217,0.06)",
        border: `1px solid ${isEditing ? NW_BRAND.mauveBorder : NW_BRAND.faintLine}`,
        transition: "border-color 0.18s",
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          background: `linear-gradient(135deg, ${NW_BRAND.bgMid}, ${NW_BRAND.bgLight})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 17,
          flexShrink: 0,
          boxShadow: `0 0 10px ${NW_BRAND.mauveDim}`,
        }}
      >
        {icon}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: NW_BRAND.textMuted,
            textTransform: "uppercase",
            letterSpacing: 1.2,
            marginBottom: 3,
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
          }}
        >
          {label}
        </div>

        {!isEditing ? (
          // ── Mode lecture : valeur + bouton ✎ ──────────────────────────────
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: value ? NW_BRAND.textPrimary : NW_BRAND.textLight,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                wordBreak: "break-word",
                flex: 1,
              }}
            >
              {value || "—"}
            </div>
            <button
              onClick={handleEditClick}
              title={`Modifier ${label.toLowerCase()}`}
              style={{
                width: 26,
                height: 26,
                borderRadius: 8,
                border: `1px solid ${NW_BRAND.mauveBorder}`,
                background: NW_BRAND.mauveDim,
                color: NW_BRAND.textSecondary,
                fontSize: 12,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "all 0.15s",
                padding: 0,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  "rgba(125,168,217,0.28)";
                (e.currentTarget as HTMLElement).style.borderColor =
                  NW_BRAND.mauve;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  NW_BRAND.mauveDim;
                (e.currentTarget as HTMLElement).style.borderColor =
                  NW_BRAND.mauveBorder;
              }}
            >
              ✎
            </button>
          </div>
        ) : (
          // ── Mode édition : input + ✕ (annuler) + ✓ (valider) ──────────────
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                ref={inputRef}
                type="text"
                value={draft}
                disabled={saving}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 14,
                  fontWeight: 600,
                  color: NW_BRAND.textPrimary,
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                  padding: "7px 10px",
                  borderRadius: 8,
                  border: `1.5px solid ${NW_BRAND.mauveBorder}`,
                  outline: "none",
                  background: NW_BRAND.white,
                }}
              />
              <button
                onClick={handleCancel}
                disabled={saving}
                title="Annuler"
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 8,
                  border: "1.5px solid rgba(214,69,69,0.4)",
                  background: "rgba(214,69,69,0.10)",
                  color: "#D64545",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: saving ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transition: "all 0.15s",
                  padding: 0,
                  opacity: saving ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!saving)
                    (e.currentTarget as HTMLElement).style.background =
                      "rgba(214,69,69,0.22)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    "rgba(214,69,69,0.10)";
                }}
              >
                ✕
              </button>
              <button
                onClick={handleConfirm}
                disabled={saving}
                title="Valider"
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 8,
                  border: "none",
                  background: `linear-gradient(135deg, ${NW_BRAND.bgMid}, ${NW_BRAND.bgLight})`,
                  color: NW_BRAND.white,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: saving ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transition: "all 0.15s",
                  padding: 0,
                  opacity: saving ? 0.75 : 1,
                  boxShadow: `0 2px 8px rgba(19,59,110,0.35)`,
                }}
                onMouseEnter={(e) => {
                  if (!saving)
                    (e.currentTarget as HTMLElement).style.opacity = "0.88";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = saving
                    ? "0.75"
                    : "1";
                }}
              >
                {saving ? <Spinner size={12} color={NW_BRAND.white} /> : "✓"}
              </button>
            </div>
            {error && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  color: NW_BRAND.danger,
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                }}
              >
                ⚠ {error}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Popup choix source photo
// ─────────────────────────────────────────────────────────────────────────────
interface PhotoPickerPopupProps {
  onCamera: () => void;
  onGallery: () => void;
  onClose: () => void;
}
const PhotoPickerPopup: React.FC<PhotoPickerPopupProps> = ({
  onCamera,
  onGallery,
  onClose,
}) => (
  <div
    onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(13,42,80,0.65)",
      backdropFilter: "blur(6px)",
      animation: "nwBgFadeIn 0.2s ease-out",
      padding: 16,
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth: 340,
        background: NW_BRAND.white,
        borderRadius: 20,
        padding: "28px 24px 22px",
        boxShadow:
          "0 0 0 1px rgba(24,75,140,0.12), 0 24px 70px rgba(15,23,42,0.35)",
        animation: "nwPopUp 0.35s cubic-bezier(0.22,1,0.36,1) both",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: 22 }}>
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${NW_BRAND.bgMid}, ${NW_BRAND.bgLight})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 12px",
            boxShadow: `0 0 20px ${NW_BRAND.mauveDim}`,
          }}
        >
          <CameraIcon size={22} color={NW_BRAND.mauve} />
        </div>
        <h3
          style={{
            margin: 0,
            fontSize: 17,
            fontWeight: 800,
            color: NW_BRAND.textPrimary,
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
          }}
        >
          Changer la photo
        </h3>
        <p
          style={{
            margin: "5px 0 0",
            fontSize: 12.5,
            color: NW_BRAND.textMuted,
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
          }}
        >
          Choisissez la source de votre photo
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button
          onClick={onCamera}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "13px 18px",
            border: `1.5px solid ${NW_BRAND.mauveBorder}`,
            borderRadius: 12,
            background: NW_BRAND.mauveDim,
            cursor: "pointer",
            textAlign: "left",
            transition: "all 0.15s",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background =
              "rgba(125,168,217,0.28)";
            (e.currentTarget as HTMLElement).style.borderColor = NW_BRAND.mauve;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background =
              NW_BRAND.mauveDim;
            (e.currentTarget as HTMLElement).style.borderColor =
              NW_BRAND.mauveBorder;
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: `linear-gradient(135deg, ${NW_BRAND.bgMid}, ${NW_BRAND.bgLight})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CameraIcon size={17} color={NW_BRAND.mauve} />
          </div>
          <div>
            <div
              style={{
                fontSize: 13.5,
                fontWeight: 700,
                color: NW_BRAND.textPrimary,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              }}
            >
              Ouvrir la caméra
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: NW_BRAND.textMuted,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                marginTop: 2,
              }}
            >
              Prendre une photo en direct
            </div>
          </div>
        </button>

        <button
          onClick={onGallery}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "13px 18px",
            border: `1.5px solid ${NW_BRAND.mauveBorder}`,
            borderRadius: 12,
            background: NW_BRAND.mauveDim,
            cursor: "pointer",
            textAlign: "left",
            transition: "all 0.15s",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background =
              "rgba(125,168,217,0.28)";
            (e.currentTarget as HTMLElement).style.borderColor = NW_BRAND.mauve;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background =
              NW_BRAND.mauveDim;
            (e.currentTarget as HTMLElement).style.borderColor =
              NW_BRAND.mauveBorder;
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: `linear-gradient(135deg, ${NW_BRAND.bgMid}, ${NW_BRAND.bgLight})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontSize: 18,
            }}
          >
            🖼️
          </div>
          <div>
            <div
              style={{
                fontSize: 13.5,
                fontWeight: 700,
                color: NW_BRAND.textPrimary,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              }}
            >
              Galerie du PC
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: NW_BRAND.textMuted,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                marginTop: 2,
              }}
            >
              Choisir depuis vos fichiers
            </div>
          </div>
        </button>

        <button
          onClick={onClose}
          style={{
            width: "100%",
            padding: "10px",
            border: `1.5px solid ${NW_BRAND.faintLine}`,
            borderRadius: 12,
            background: "transparent",
            color: NW_BRAND.textMuted,
            fontSize: 13,
            fontWeight: 700,
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            cursor: "pointer",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background =
              "rgba(125,168,217,0.08)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background = "transparent";
          }}
        >
          Annuler
        </button>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Modal caméra — accès direct webcam via getUserMedia
// ─────────────────────────────────────────────────────────────────────────────
interface CameraCaptureModalProps {
  onCapture: (file: File) => void;
  onClose: () => void;
}

const CameraCaptureModal: React.FC<CameraCaptureModalProps> = ({
  onCapture,
  onClose,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [camError, setCamError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [captured, setCaptured] = useState<string | null>(null); // base64 preview

  // Démarrer le flux caméra
  useEffect(() => {
    let active = true;
    setCamError(null);
    setReady(false);

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "user", width: 1280, height: 720 } })
      .then((stream) => {
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current?.play();
            setReady(true);
          };
        }
      })
      .catch((err) => {
        if (!active) return;
        console.error("[CameraCapture] getUserMedia error", err);
        if (
          err.name === "NotAllowedError" ||
          err.name === "PermissionDeniedError"
        ) {
          setCamError(
            "Accès à la caméra refusé. Autorisez l'accès dans les paramètres du navigateur.",
          );
        } else if (
          err.name === "NotFoundError" ||
          err.name === "DevicesNotFoundError"
        ) {
          setCamError("Aucune caméra détectée sur cet appareil.");
        } else {
          setCamError("Impossible d'accéder à la caméra : " + err.message);
        }
      });

    return () => {
      active = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  // Arrêter le flux quand on ferme
  const handleClose = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    onClose();
  };

  // Capturer une frame depuis la vidéo
  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    setCaptured(dataUrl);

    // Arrêter le flux après capture
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  // Retenter (relancer la caméra)
  const handleRetake = () => {
    setCaptured(null);
    setReady(false);
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "user", width: 1280, height: 720 } })
      .then((stream) => {
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current?.play();
            setReady(true);
          };
        }
      })
      .catch((err) => {
        setCamError("Impossible de relancer la caméra : " + err.message);
      });
  };

  // Confirmer et envoyer le fichier
  const handleConfirm = () => {
    if (!captured) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `photo_${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        onCapture(file);
      },
      "image/jpeg",
      0.92,
    );
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(13,42,80,0.82)",
        backdropFilter: "blur(8px)",
        animation: "nwBgFadeIn 0.2s ease-out",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          background: NW_BRAND.white,
          borderRadius: 20,
          overflow: "hidden",
          boxShadow:
            "0 0 0 1px rgba(24,75,140,0.14), 0 32px 80px rgba(15,23,42,0.45)",
          animation: "nwPopUp 0.35s cubic-bezier(0.22,1,0.36,1) both",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: `linear-gradient(135deg, ${NW_BRAND.bgDeep}, ${NW_BRAND.bgMid})`,
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: NW_BRAND.mauveDim,
                border: `1px solid ${NW_BRAND.mauveBorder}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CameraIcon size={16} color={NW_BRAND.mauve} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: NW_BRAND.white,
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                }}
              >
                {captured ? "Confirmer la photo" : "Prendre une photo"}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "rgba(125,168,217,0.70)",
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                }}
              >
                {captured
                  ? "Validez ou reprenez"
                  : ready
                    ? "Caméra active — centrez votre visage"
                    : camError
                      ? "Erreur caméra"
                      : "Initialisation…"}
              </div>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: `1px solid ${NW_BRAND.mauveBorder}`,
              background: NW_BRAND.mauveDim,
              color: NW_BRAND.dimWhite,
              fontSize: 14,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "monospace",
              transition: "all 0.15s",
            }}
            title="Fermer"
          >
            ✕
          </button>
        </div>

        {/* Zone vidéo / preview */}
        <div
          style={{
            position: "relative",
            background: "#1D5BA8",
            aspectRatio: "4/3",
            overflow: "hidden",
          }}
        >
          {/* Vidéo en direct (cachée si capture effectuée) */}
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: captured ? "none" : "block",
              transform: "scaleX(-1)", // effet miroir naturel
            }}
          />

          {/* Preview capture */}
          {captured && (
            <img
              src={captured}
              alt="Capture"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          )}

          {/* Loader / erreur */}
          {!ready && !camError && !captured && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                color: NW_BRAND.mauve,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              }}
            >
              <Spinner size={32} color={NW_BRAND.mauve} />
              <span style={{ fontSize: 13, opacity: 0.8 }}>
                Accès à la caméra…
              </span>
            </div>
          )}

          {camError && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 14,
                padding: 24,
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 36 }}>📷</div>
              <div
                style={{
                  fontSize: 13,
                  color: "rgba(214,69,69,0.90)",
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                  lineHeight: 1.5,
                  maxWidth: 320,
                }}
              >
                {camError}
              </div>
            </div>
          )}

          {/* Cadre viseur (seulement quand caméra active) */}
          {ready && !captured && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* Coins du viseur */}
              {[
                { top: "20%", left: "28%", borderTop: true, borderLeft: true },
                {
                  top: "20%",
                  right: "28%",
                  borderTop: true,
                  borderRight: true,
                },
                {
                  bottom: "20%",
                  left: "28%",
                  borderBottom: true,
                  borderLeft: true,
                },
                {
                  bottom: "20%",
                  right: "28%",
                  borderBottom: true,
                  borderRight: true,
                },
              ].map((pos, i) => (
                <div
                  key={i}
                  style={{
                    position: "absolute",
                    width: 22,
                    height: 22,
                    borderTop: (pos as any).borderTop
                      ? `2px solid ${NW_BRAND.mauve}CC`
                      : "none",
                    borderLeft: (pos as any).borderLeft
                      ? `2px solid ${NW_BRAND.mauve}CC`
                      : "none",
                    borderRight: (pos as any).borderRight
                      ? `2px solid ${NW_BRAND.mauve}CC`
                      : "none",
                    borderBottom: (pos as any).borderBottom
                      ? `2px solid ${NW_BRAND.mauve}CC`
                      : "none",
                    top: (pos as any).top,
                    left: (pos as any).left,
                    right: (pos as any).right,
                    bottom: (pos as any).bottom,
                    borderRadius: 2,
                  }}
                />
              ))}
            </div>
          )}

          {/* Canvas caché pour la capture */}
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </div>

        {/* Boutons d'action */}
        <div
          style={{
            padding: "16px 20px",
            display: "flex",
            gap: 10,
            background: NW_BRAND.white,
            borderTop: `1px solid ${NW_BRAND.faintLine}`,
          }}
        >
          {!captured ? (
            <>
              <button
                onClick={handleClose}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: 10,
                  border: `1.5px solid ${NW_BRAND.faintLine}`,
                  background: "transparent",
                  color: NW_BRAND.textMuted,
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    "rgba(125,168,217,0.08)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    "transparent";
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleCapture}
                disabled={!ready}
                style={{
                  flex: 2,
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "none",
                  background: ready
                    ? `linear-gradient(135deg, ${NW_BRAND.bgMid}, ${NW_BRAND.bgLight})`
                    : "rgba(125,168,217,0.15)",
                  color: ready ? NW_BRAND.white : "rgba(125,168,217,0.45)",
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                  cursor: ready ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  transition: "all 0.18s",
                  boxShadow: ready ? `0 4px 14px rgba(19,59,110,0.35)` : "none",
                }}
                onMouseEnter={(e) => {
                  if (ready)
                    (e.currentTarget as HTMLElement).style.opacity = "0.88";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = "1";
                }}
              >
                <CameraIcon
                  size={14}
                  color={ready ? "#FFFFFF" : "rgba(125,168,217,0.45)"}
                />
                Prendre la photo
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleRetake}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: 10,
                  border: `1.5px solid ${NW_BRAND.mauveBorder}`,
                  background: NW_BRAND.mauveDim,
                  color: NW_BRAND.textSecondary,
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    "rgba(125,168,217,0.28)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    NW_BRAND.mauveDim;
                }}
              >
                🔄 Reprendre
              </button>
              <button
                onClick={handleConfirm}
                style={{
                  flex: 2,
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "none",
                  background: `linear-gradient(135deg, ${NW_BRAND.bgMid}, ${NW_BRAND.bgLight})`,
                  color: NW_BRAND.white,
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  transition: "all 0.18s",
                  boxShadow: `0 4px 14px rgba(19,59,110,0.35)`,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = "0.88";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = "1";
                }}
              >
                ✓ Utiliser cette photo
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CropModal — recadrage circulaire style Facebook
// ─────────────────────────────────────────────────────────────────────────────
interface CropModalProps {
  imageSrc: string; // dataURL de l'image source
  onConfirm: (file: File) => void;
  onCancel: () => void; // → retour au PhotoPickerPopup
}

const CROP_CIRCLE_SIZE = 280; // diamètre du cercle de recadrage (px)

const CropModal: React.FC<CropModalProps> = ({
  imageSrc,
  onConfirm,
  onCancel,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [showConfirm, setShowConfirm] = useState(false);
  const [croppedDataUrl, setCroppedDataUrl] = useState<string | null>(null);

  const dragStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  // Charger les dimensions naturelles + zoom initial
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
      const initZoom =
        CROP_CIRCLE_SIZE / Math.min(img.naturalWidth, img.naturalHeight);
      setZoom(initZoom);
      setPos({ x: 0, y: 0 });
    };
    img.src = imageSrc;
  }, [imageSrc]);

  const minZoom =
    naturalSize.w > 0
      ? CROP_CIRCLE_SIZE / Math.min(naturalSize.w, naturalSize.h)
      : 1;
  const maxZoom = minZoom * 4;

  const dispW = naturalSize.w * zoom;
  const dispH = naturalSize.h * zoom;

  // Clamp : l'image doit toujours couvrir le cercle
  const clamp = (x: number, y: number, z: number) => {
    const dw = naturalSize.w * z;
    const dh = naturalSize.h * z;
    const mx = Math.max(0, (dw - CROP_CIRCLE_SIZE) / 2);
    const my = Math.max(0, (dh - CROP_CIRCLE_SIZE) / 2);
    return {
      x: Math.max(-mx, Math.min(mx, x)),
      y: Math.max(-my, Math.min(my, y)),
    };
  };

  // ── Drag mouse ──
  const onMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    dragStart.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
    e.preventDefault();
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    const dx = e.clientX - dragStart.current.mx;
    const dy = e.clientY - dragStart.current.my;
    setPos(clamp(dragStart.current.px + dx, dragStart.current.py + dy, zoom));
  };
  const onMouseUp = () => setDragging(false);

  // ── Drag touch ──
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    setDragging(true);
    dragStart.current = { mx: t.clientX, my: t.clientY, px: pos.x, py: pos.y };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!dragging) return;
    const t = e.touches[0];
    setPos(
      clamp(
        dragStart.current.px + (t.clientX - dragStart.current.mx),
        dragStart.current.py + (t.clientY - dragStart.current.my),
        zoom,
      ),
    );
  };

  // ── Zoom slider ──
  const handleZoom = (z: number) => {
    setZoom(z);
    setPos(clamp(pos.x, pos.y, z));
  };

  // ── Capture la zone recadrée dans un canvas ──
  const handleValidate = () => {
    const canvas = canvasRef.current;
    if (!canvas || naturalSize.w === 0) return;
    canvas.width = CROP_CIRCLE_SIZE;
    canvas.height = CROP_CIRCLE_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clip circulaire
    ctx.beginPath();
    ctx.arc(
      CROP_CIRCLE_SIZE / 2,
      CROP_CIRCLE_SIZE / 2,
      CROP_CIRCLE_SIZE / 2,
      0,
      Math.PI * 2,
    );
    ctx.clip();

    const img = new Image();
    img.onload = () => {
      // Coordonnées source (image naturelle) correspondant au cercle affiché
      const srcX =
        naturalSize.w / 2 - CROP_CIRCLE_SIZE / 2 / zoom - pos.x / zoom;
      const srcY =
        naturalSize.h / 2 - CROP_CIRCLE_SIZE / 2 / zoom - pos.y / zoom;
      const srcW = CROP_CIRCLE_SIZE / zoom;
      const srcH = CROP_CIRCLE_SIZE / zoom;
      ctx.drawImage(
        img,
        srcX,
        srcY,
        srcW,
        srcH,
        0,
        0,
        CROP_CIRCLE_SIZE,
        CROP_CIRCLE_SIZE,
      );
      setCroppedDataUrl(canvas.toDataURL("image/jpeg", 0.92));
      setShowConfirm(true);
    };
    img.src = imageSrc;
  };

  // ── Confirmer définitivement ──
  const handleConfirmFinal = () => {
    const canvas = canvasRef.current;
    if (!canvas || !croppedDataUrl) return;
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        onConfirm(
          new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" }),
        );
      },
      "image/jpeg",
      0.92,
    );
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10001,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(13,42,80,0.90)",
        backdropFilter: "blur(10px)",
        animation: "nwBgFadeIn 0.2s ease-out",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          background: `linear-gradient(180deg, #1D5BA8 0%, #1D5BA8 100%)`,
          borderRadius: 20,
          overflow: "hidden",
          border: `1px solid ${NW_BRAND.mauveBorder}`,
          boxShadow:
            "0 0 0 1px rgba(125,168,217,0.12), 0 32px 80px rgba(15,23,42,0.65)",
          animation: "nwPopUp 0.35s cubic-bezier(0.22,1,0.36,1) both",
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            background: `linear-gradient(135deg, ${NW_BRAND.bgDeep}, ${NW_BRAND.bgMid})`,
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${NW_BRAND.mauveBorder}`,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 800,
                color: NW_BRAND.white,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              }}
            >
              Recadrer la photo
            </div>
            <div
              style={{
                fontSize: 11,
                color: "rgba(125,168,217,0.70)",
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                marginTop: 2,
              }}
            >
              Faites glisser pour repositionner · utilisez le curseur pour
              zoomer
            </div>
          </div>
          {/* ✕ rouge */}
          <button
            onClick={onCancel}
            title="Annuler"
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              border: "none",
              background: "#D64545",
              color: "#FFFFFF",
              fontSize: 15,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 10px rgba(214,69,69,0.55)",
              transition: "all 0.15s",
              fontWeight: 700,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#B83838";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#D64545";
            }}
          >
            ✕
          </button>
        </div>

        {/* ── Zone de recadrage ── */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "24px 20px 20px",
            gap: 18,
          }}
        >
          {/* Conteneur drag */}
          <div
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onMouseUp}
            style={{
              position: "relative",
              width: "100%",
              maxWidth: 460,
              height: 360,
              overflow: "hidden",
              cursor: dragging ? "grabbing" : "grab",
              borderRadius: 14,
              background: "#1D5BA8",
              userSelect: "none",
            }}
          >
            {/* Image déplaçable */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: dispW,
                height: dispH,
                transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px))`,
                pointerEvents: "none",
              }}
            >
              <img
                src={imageSrc}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  pointerEvents: "none",
                }}
                draggable={false}
              />
            </div>

            {/* Masque sombre + cercle transparent */}
            <svg
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            >
              <defs>
                <mask id="nwCropHole">
                  <rect width="100%" height="100%" fill="white" />
                  <circle
                    cx="50%"
                    cy="50%"
                    r={CROP_CIRCLE_SIZE / 2}
                    fill="black"
                  />
                </mask>
              </defs>
              <rect
                width="100%"
                height="100%"
                fill="rgba(15,23,42,0.58)"
                mask="url(#nwCropHole)"
              />
              <circle
                cx="50%"
                cy="50%"
                r={CROP_CIRCLE_SIZE / 2}
                fill="none"
                stroke={`${NW_BRAND.mauve}BB`}
                strokeWidth="2"
                strokeDasharray="6 4"
              />
            </svg>

            {/* Label hint */}
            <div
              style={{
                position: "absolute",
                bottom: 12,
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(15,23,42,0.65)",
                borderRadius: 99,
                padding: "4px 14px",
                fontSize: 11,
                color: "rgba(255,255,255,0.75)",
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                whiteSpace: "nowrap",
                pointerEvents: "none",
              }}
            >
              ✥ Faites glisser pour repositionner
            </div>
          </div>

          {/* Slider zoom */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              width: "100%",
              maxWidth: 460,
            }}
          >
            <span
              style={{
                fontSize: 18,
                color: NW_BRAND.dimWhite,
                userSelect: "none",
                lineHeight: 1,
              }}
            >
              −
            </span>
            <input
              type="range"
              min={minZoom}
              max={maxZoom}
              step={0.005}
              value={zoom}
              onChange={(e) => handleZoom(Number(e.target.value))}
              style={{
                flex: 1,
                accentColor: NW_BRAND.mauve,
                cursor: "pointer",
                height: 4,
              }}
            />
            <span
              style={{
                fontSize: 18,
                color: NW_BRAND.dimWhite,
                userSelect: "none",
                lineHeight: 1,
              }}
            >
              +
            </span>
          </div>

          <canvas ref={canvasRef} style={{ display: "none" }} />
        </div>

        {/* ── Boutons ── */}
        <div
          style={{
            padding: "14px 20px 18px",
            display: "flex",
            gap: 10,
            borderTop: `1px solid ${NW_BRAND.mauveBorder}`,
          }}
        >
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 10,
              border: "1.5px solid rgba(214,69,69,0.4)",
              background: "rgba(214,69,69,0.10)",
              color: "#D64545",
              fontSize: 13,
              fontWeight: 700,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background =
                "rgba(214,69,69,0.22)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background =
                "rgba(214,69,69,0.10)";
            }}
          >
            ✕ Annuler
          </button>
          <button
            onClick={handleValidate}
            style={{
              flex: 2,
              padding: "11px 16px",
              borderRadius: 10,
              border: "none",
              background: `linear-gradient(135deg, ${NW_BRAND.bgMid}, ${NW_BRAND.bgLight})`,
              color: NW_BRAND.white,
              fontSize: 13,
              fontWeight: 700,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              boxShadow: `0 4px 14px rgba(19,59,110,0.4)`,
              transition: "all 0.18s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.opacity = "0.88";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.opacity = "1";
            }}
          >
            ✓ Valider le recadrage
          </button>
        </div>
      </div>

      {/* ── Popup confirmation finale ── */}
      {showConfirm && croppedDataUrl && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10002,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(13,42,80,0.78)",
            backdropFilter: "blur(6px)",
            animation: "nwBgFadeIn 0.15s ease-out",
            padding: 16,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 340,
              background: NW_BRAND.white,
              borderRadius: 20,
              padding: "28px 24px 22px",
              boxShadow:
                "0 0 0 1px rgba(24,75,140,0.12), 0 24px 70px rgba(15,23,42,0.45)",
              animation: "nwPopUp 0.30s cubic-bezier(0.22,1,0.36,1) both",
              textAlign: "center",
            }}
          >
            {/* Aperçu circulaire */}
            <img
              src={croppedDataUrl}
              alt="aperçu"
              style={{
                width: 110,
                height: 110,
                borderRadius: "50%",
                objectFit: "cover",
                display: "block",
                margin: "0 auto 16px",
                border: `3px solid ${NW_BRAND.mauveBorder}`,
                boxShadow: `0 0 24px ${NW_BRAND.mauveDim}`,
              }}
            />
            <div
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: NW_BRAND.textPrimary,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                marginBottom: 6,
              }}
            >
              Confirmer cette photo ?
            </div>
            <div
              style={{
                fontSize: 12,
                color: NW_BRAND.textMuted,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                marginBottom: 22,
              }}
            >
              Votre photo de profil sera mise à jour
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setShowConfirm(false)}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: 10,
                  border: `1.5px solid ${NW_BRAND.mauveBorder}`,
                  background: NW_BRAND.mauveDim,
                  color: NW_BRAND.textSecondary,
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    "rgba(125,168,217,0.28)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    NW_BRAND.mauveDim;
                }}
              >
                ✎ Modifier
              </button>
              <button
                onClick={handleConfirmFinal}
                style={{
                  flex: 2,
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "none",
                  background: `linear-gradient(135deg, ${NW_BRAND.bgMid}, ${NW_BRAND.bgLight})`,
                  color: NW_BRAND.white,
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                  cursor: "pointer",
                  boxShadow: `0 4px 14px rgba(19,59,110,0.35)`,
                  transition: "all 0.18s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = "0.88";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = "1";
                }}
              >
                ✓ Valider
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── LangueField — liste déroulante ───────────────────────────────────────────
const LangueField: React.FC<{ user: Record<string, any> | null }> = ({
  user,
}) => {
  const LANGUES = [
    { id: "fr", label: "Français", flag: "🇫🇷" },
    { id: "en", label: "English", flag: "🇬🇧" },
    { id: "ar", label: "Arabe", flag: "🇸🇦" },
  ];

  const rawId =
    user?.langue ||
    user?.Langue ||
    user?.idLangue ||
    user?.IdLangue ||
    user?.id_langue ||
    user?.langueid ||
    user?.LangueId ||
    user?.languageId ||
    user?.language;

  const map: Record<string, string> = {
    fr: "Français",
    en: "English",
    ar: "Arabe",
  };
  const initLabel = rawId ? (map[String(rawId)] ?? String(rawId)) : "Français";

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(initLabel);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        padding: "14px 20px",
        borderRadius: 12,
        background: "rgba(125,168,217,0.06)",
        border: `1px solid ${open ? NW_BRAND.mauveBorder : NW_BRAND.faintLine}`,
        transition: "border-color 0.18s",
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          flexShrink: 0,
          background: `linear-gradient(135deg, ${NW_BRAND.bgMid}, ${NW_BRAND.bgLight})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 17,
          boxShadow: `0 0 10px ${NW_BRAND.mauveDim}`,
        }}
      >
        🌐
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: NW_BRAND.textMuted,
            textTransform: "uppercase",
            letterSpacing: 1.2,
            marginBottom: 3,
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
          }}
        >
          Langue
        </div>

        <button
          onClick={() => setOpen((o) => !o)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 0,
            width: "100%",
          }}
        >
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: NW_BRAND.textPrimary,
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
          >
            {selected}
          </span>
          <span
            style={{
              marginLeft: "auto",
              fontSize: 11,
              color: NW_BRAND.textMuted,
              display: "inline-block",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          >
            ▼
          </span>
        </button>

        {open && (
          <div
            style={{
              marginTop: 8,
              borderRadius: 10,
              overflow: "hidden",
              border: `1px solid ${NW_BRAND.mauveBorder}`,
              boxShadow: "0 8px 24px rgba(19,59,110,0.12)",
            }}
          >
            {LANGUES.map((l) => (
              <button
                key={l.id}
                onClick={() => {
                  setSelected(l.label);
                  setOpen(false);
                }}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 14px",
                  border: "none",
                  cursor: "pointer",
                  background:
                    selected === l.label
                      ? "rgba(125,168,217,0.14)"
                      : "rgba(125,168,217,0.04)",
                  textAlign: "left",
                  transition: "background 0.13s",
                  borderBottom: `1px solid ${NW_BRAND.faintLine}`,
                }}
                onMouseEnter={(e) => {
                  if (selected !== l.label)
                    (e.currentTarget as HTMLElement).style.background =
                      "rgba(125,168,217,0.10)";
                }}
                onMouseLeave={(e) => {
                  if (selected !== l.label)
                    (e.currentTarget as HTMLElement).style.background =
                      "rgba(125,168,217,0.04)";
                }}
              >
                <span style={{ fontSize: 18 }}>{l.flag}</span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: selected === l.label ? 700 : 600,
                    color:
                      selected === l.label
                        ? NW_BRAND.textSecondary
                        : NW_BRAND.textPrimary,
                    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                  }}
                >
                  {l.label}
                </span>
                {selected === l.label && (
                  <span
                    style={{
                      marginLeft: "auto",
                      color: NW_BRAND.mauveGlow,
                      fontSize: 13,
                    }}
                  >
                    ✓
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ProfilePage principal
// ─────────────────────────────────────────────────────────────────────────────
interface ProfilePageProps {
  onLogout?: () => void;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ onLogout }) => {
  const user = useMemo(() => getBiUser(), []);

  // ── Photo : restaure depuis bi_photo_{username} si bi_user ne l'a pas ────────
  const [photoUrl, setPhotoUrl] = useState<string>(() =>
    loadPersistedPhoto(user),
  );
  const [showPicker, setShowPicker] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);

  // ── Champs éditables (Email / Téléphone) ──────────────────────────────────
  const [emailValue, setEmailValue] = useState<string>(
    user?.email || user?.Email || "",
  );
  const [phoneValue, setPhoneValue] = useState<string>(
    user?.phone || user?.Phone || "",
  );

  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // ── Ouvre le CropModal à partir d'un File ou dataURL ─────────────────────────
  const openCropModal = useCallback((fileOrDataUrl: File | string) => {
    if (typeof fileOrDataUrl === "string") {
      setCropImageSrc(fileOrDataUrl);
      setShowCropModal(true);
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setCropImageSrc(ev.target?.result as string);
        setShowCropModal(true);
      };
      reader.readAsDataURL(fileOrDataUrl);
    }
  }, []);

  // ── Upload via backend Avsoft → PieceJointe/upload/photo ─────────────────────
  // Réponse : { success, data: { Id, Url, NomFich, TypeFich, Categorie, DateCrea } }
  const handleFileSelected = useCallback(
    async (file: File) => {
      setShowPicker(false);
      setUploadMsg(null);

      if (!file.type.startsWith("image/")) {
        setUploadMsg({
          kind: "error",
          text: "Veuillez sélectionner une image.",
        });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setUploadMsg({ kind: "error", text: "Image trop grande (max 10 Mo)." });
        return;
      }

      setUploading(true);
      try {
        // Upload multipart vers le backend Avsoft (URL issue de parametre_bi)
        const newPhotoUrl = await uploadProfilePhoto(file);

        if (!newPhotoUrl)
          throw new Error("URL de la photo non retournée par le serveur.");

        // 4. Double persistance :
        //    a) bi_user.photoUrl → session courante
        //    b) bi_photo_{username} → clé dédiée, survit au logout/login
        savePhotoUrl(user, newPhotoUrl);

        // 5. Mise à jour UI
        setPhotoUrl(newPhotoUrl);
        setUploadMsg({
          kind: "success",
          text: "Photo mise à jour avec succès !",
        });
        setTimeout(() => setUploadMsg(null), 3000);
      } catch (err) {
        console.error("[ProfilePage] upload error", err);
        setUploadMsg({
          kind: "error",
          text: getErrorMessage(err, "Erreur lors de l'upload. Réessayez."),
        });
      } finally {
        setUploading(false);
      }
    },
    [user],
  );

  // ── Sauvegarde d'un champ éditable (email / téléphone) ────────────────────
  // PUT {BI_API_URL}/profile/update-user → relayé par UtilisateurBiController
  // vers {url_backend_bi}/Utilisateur/update-user (UpdateUserDto : userId, email, phone)
  const handleSaveField = useCallback(
    async (fieldKey: string, newValue: string) => {
      const userId =
        user?.id ||
        user?.Id ||
        user?.userId ||
        user?.UserId ||
        user?.username ||
        user?.Username ||
        "";

      if (!userId) {
        throw new Error("Identifiant utilisateur introuvable.");
      }

      try {
        await updateUserProfile({
          userId: String(userId),
          email: fieldKey === "email" ? newValue : emailValue,
          phone: fieldKey === "phone" ? newValue : phoneValue,
        });
      } catch (err) {
        throw new Error(getErrorMessage(err, "Échec de la mise à jour."));
      }

      // Mise à jour état local + bi_user (localStorage)
      if (fieldKey === "email") setEmailValue(newValue);
      if (fieldKey === "phone") setPhoneValue(newValue);

      const current = getBiUser() ?? {};
      saveBiUser({ ...current, [fieldKey]: newValue });

      setUploadMsg({
        kind: "success",
        text: "Profil mis à jour avec succès !",
      });
      setTimeout(() => setUploadMsg(null), 3000);
    },
    [user, emailValue, phoneValue],
  );

  const handleOpenGallery = () => {
    setShowPicker(false);
    setTimeout(() => galleryInputRef.current?.click(), 100);
  };
  const handleOpenCamera = () => {
    setShowPicker(false);
    // Utiliser getUserMedia si disponible (PC/desktop) sinon fallback input capture
    if (
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function"
    ) {
      setShowCameraModal(true);
    } else {
      setTimeout(() => cameraInputRef.current?.click(), 100);
    }
  };

  // ── Logout — NE PAS supprimer bi_photo_{username} → restaurée au prochain login
  const handleLogout = () => {
    if (onLogout) onLogout();
  };

  const displayName =
    user?.fullName ||
    user?.FullName ||
    user?.username ||
    user?.Username ||
    "Utilisateur";

  const initials = displayName
    .split(" ")
    .map((w: string) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <>
      <style>{`
        @keyframes nwSpin { to { transform: rotate(360deg); } }
        @keyframes nwBgFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes nwPopUp {
          from { opacity: 0; transform: scale(0.92) translateY(16px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }
        @keyframes nwProfileFadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes nwGlow {
          0%, 100% { box-shadow: 0 0 24px rgba(74,134,199,0.30), 0 0 0 4px rgba(125,168,217,0.18); }
          50%       { box-shadow: 0 0 40px rgba(74,134,199,0.55), 0 0 0 4px rgba(125,168,217,0.30); }
        }
        .nw-profile-card { animation: nwProfileFadeUp 0.45s cubic-bezier(0.22,1,0.36,1) both; }
        .nw-profile-card:nth-child(2) { animation-delay: 0.08s; }
        .nw-profile-card:nth-child(3) { animation-delay: 0.16s; }
        .nw-logout-btn:hover {
          background: ${NW_BRAND.danger} !important;
          color: #FFFFFF !important;
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(214,69,69,0.35) !important;
        }
        .nw-camera-btn:hover { opacity: 1 !important; transform: scale(1.08); }
        .nw-camera-btn:active { transform: scale(0.96); }
      `}</style>

      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) openCropModal(f);
          e.target.value = "";
        }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="user"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) openCropModal(f);
          e.target.value = "";
        }}
      />

      {showPicker && (
        <PhotoPickerPopup
          onCamera={handleOpenCamera}
          onGallery={handleOpenGallery}
          onClose={() => setShowPicker(false)}
        />
      )}

      {showCameraModal && (
        <CameraCaptureModal
          onCapture={(file) => {
            setShowCameraModal(false);
            openCropModal(file);
          }}
          onClose={() => setShowCameraModal(false)}
        />
      )}

      {showCropModal && cropImageSrc && (
        <CropModal
          imageSrc={cropImageSrc}
          onConfirm={(file) => {
            setShowCropModal(false);
            setCropImageSrc(null);
            handleFileSelected(file);
          }}
          onCancel={() => {
            setShowCropModal(false);
            setCropImageSrc(null);
            setShowPicker(true);
          }}
        />
      )}

      <div
        style={{
          minHeight: "calc(100vh - 62px)",
          background: NW_BRAND.white,
          padding: "40px 24px 60px",
          fontFamily: "Inter, system-ui, -apple-system, sans-serif",
        }}
      >
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          {/* Header */}
          <div className="nw-profile-card" style={{ marginBottom: 32 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 4,
              }}
            >
              <div
                style={{
                  width: 3,
                  height: 22,
                  borderRadius: 99,
                  background: `linear-gradient(180deg, ${NW_BRAND.bgMid}, ${NW_BRAND.mauve})`,
                }}
              />
              <h1
                style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 800,
                  color: NW_BRAND.textPrimary,
                  letterSpacing: -0.3,
                }}
              >
                Mon Profil
              </h1>
            </div>
            <p
              style={{
                margin: "0 0 0 13px",
                fontSize: 13,
                color: NW_BRAND.textMuted,
              }}
            >
              Vos informations de compte AvDash
            </p>
          </div>

          {/* Message upload */}
          {uploadMsg && (
            <div
              style={{
                marginBottom: 16,
                padding: "10px 16px",
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 8,
                background:
                  uploadMsg.kind === "success"
                    ? NW_BRAND.successLight
                    : NW_BRAND.dangerLight,
                color:
                  uploadMsg.kind === "success"
                    ? NW_BRAND.success
                    : NW_BRAND.danger,
                border: `1px solid ${uploadMsg.kind === "success" ? NW_BRAND.success + "44" : NW_BRAND.danger + "44"}`,
                animation: "nwProfileFadeUp 0.25s ease-out",
              }}
            >
              <span>{uploadMsg.kind === "success" ? "✓" : "⚠"}</span>
              {uploadMsg.text}
            </div>
          )}

          {/* Carte avatar */}
          <div
            className="nw-profile-card"
            style={{
              background: NW_BRAND.white,
              border: `1.5px solid ${NW_BRAND.mauveBorder}`,
              borderRadius: 18,
              padding: "28px 28px 24px",
              marginBottom: 20,
              boxShadow: "0 4px 24px rgba(74,134,199,0.08)",
              display: "flex",
              alignItems: "center",
              gap: 22,
            }}
          >
            <div style={{ position: "relative", flexShrink: 0 }}>
              <div
                style={{
                  width: 76,
                  height: 76,
                  borderRadius: "50%",
                  background: photoUrl
                    ? "transparent"
                    : `linear-gradient(135deg, ${NW_BRAND.bgDeep} 0%, ${NW_BRAND.bgMid} 50%, ${NW_BRAND.bgLight} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 26,
                  fontWeight: 800,
                  color: NW_BRAND.mauve,
                  boxShadow:
                    "0 0 24px rgba(74,134,199,0.30), 0 0 0 4px rgba(125,168,217,0.18)",
                  animation: "nwGlow 3s ease-in-out infinite",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {uploading ? (
                  <Spinner size={28} color={NW_BRAND.mauve} />
                ) : photoUrl ? (
                  <img
                    src={photoUrl}
                    alt="Photo profil"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "50%",
                    }}
                  />
                ) : (
                  initials
                )}
              </div>
              <button
                className="nw-camera-btn"
                onClick={() => !uploading && setShowPicker(true)}
                disabled={uploading}
                title="Changer la photo"
                style={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${NW_BRAND.violet}, ${NW_BRAND.bgLight})`,
                  border: `2px solid ${NW_BRAND.white}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: uploading ? "not-allowed" : "pointer",
                  opacity: uploading ? 0.6 : 0.92,
                  transition: "all 0.18s",
                  padding: 0,
                  boxShadow: "0 2px 8px rgba(24,75,140,0.35)",
                }}
              >
                <CameraIcon size={13} color="#FFFFFF" />
              </button>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: NW_BRAND.textPrimary,
                  letterSpacing: -0.3,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {displayName}
              </div>
              <div
                style={{
                  marginTop: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <div
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: "#4DB3A5",
                    boxShadow: "0 0 6px #4DB3A5",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    color: NW_BRAND.textMuted,
                    fontWeight: 600,
                  }}
                >
                  Session active
                </span>
              </div>
              {(user?.role || user?.Role) && (
                <div
                  style={{
                    marginTop: 8,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "3px 10px",
                    borderRadius: 99,
                    background: NW_BRAND.mauveDim,
                    border: `1px solid ${NW_BRAND.mauveBorder}`,
                  }}
                >
                  <span style={{ fontSize: 11 }}>⬡</span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: NW_BRAND.textSecondary,
                      letterSpacing: 0.5,
                    }}
                  >
                    {user?.role || user?.Role}
                  </span>
                </div>
              )}
              <div
                style={{
                  marginTop: 10,
                  fontSize: 11,
                  color: NW_BRAND.textLight,
                  fontStyle: "italic",
                }}
              >
                {uploading
                  ? "Upload en cours…"
                  : "Cliquez sur 📷 pour changer votre photo"}
              </div>
            </div>
          </div>

          {/* Carte informations */}
          <div
            className="nw-profile-card"
            style={{
              background: NW_BRAND.white,
              border: `1.5px solid ${NW_BRAND.mauveBorder}`,
              borderRadius: 18,
              padding: "22px 22px",
              marginBottom: 20,
              boxShadow: "0 4px 24px rgba(74,134,199,0.06)",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: NW_BRAND.textSecondary,
                textTransform: "uppercase",
                letterSpacing: 1.5,
                marginBottom: 16,
                paddingBottom: 10,
                borderBottom: `1px solid ${NW_BRAND.faintLine}`,
              }}
            >
              Informations du compte
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <InfoField
                label="Identifiant"
                value={user?.username || user?.Username}
                icon="🪪"
              />
              <EditableInfoField
                label="Email"
                value={emailValue}
                icon="✉️"
                fieldKey="email"
                onSave={handleSaveField}
              />
              <EditableInfoField
                label="Téléphone"
                value={phoneValue}
                icon="📞"
                fieldKey="phone"
                onSave={handleSaveField}
              />
              <InfoField
                label="Dépôt"
                value={user?.depot || user?.Depot}
                icon="🏪"
              />

              <LangueField user={user} />
            </div>
          </div>

          {/* Carte sécurité */}
          <div
            className="nw-profile-card"
            style={{
              background: NW_BRAND.white,
              border: `1.5px solid ${NW_BRAND.mauveBorder}`,
              borderRadius: 18,
              padding: "22px 22px",
              boxShadow: "0 4px 24px rgba(74,134,199,0.06)",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: NW_BRAND.textSecondary,
                textTransform: "uppercase",
                letterSpacing: 1.5,
                marginBottom: 16,
                paddingBottom: 10,
                borderBottom: `1px solid ${NW_BRAND.faintLine}`,
              }}
            >
              Session & Sécurité
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: "rgba(214,69,69,0.08)",
                    border: "1px solid rgba(214,69,69,0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 17,
                  }}
                >
                  ⎋
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: NW_BRAND.textPrimary,
                    }}
                  >
                    Déconnexion
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: NW_BRAND.textLight,
                      marginTop: 1,
                    }}
                  >
                    Terminer la session en cours
                  </div>
                </div>
              </div>
              <button
                className="nw-logout-btn"
                onClick={handleLogout}
                style={{
                  padding: "9px 20px",
                  borderRadius: 10,
                  border: "1.5px solid rgba(214,69,69,0.35)",
                  background: "rgba(214,69,69,0.06)",
                  color: NW_BRAND.danger,
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  transition: "all 0.18s",
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ fontSize: 15 }}>⎋</span>Se déconnecter
              </button>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              textAlign: "center",
              marginTop: 32,
              fontSize: 11,
              color: NW_BRAND.textLight,
            }}
          ></div>
        </div>
      </div>
    </>
  );
};

export default ProfilePage;